import { motion } from "framer-motion";
import { Navbar, FinalCTA, Waveform, fadeUp, staggerContainer, btnMotion } from "../components/Shared";

const USE_CASES = [
  { 
    title: "Feeling overwhelmed?", 
    desc: "Access guided, evidence-based stress regulation tools or book a same-week wellness coaching session." 
  },
  { 
    title: "Navigating conflict?", 
    desc: "Get practical communication coaching to handle difficult conversations with peers or leadership." 
  },
  { 
    title: "Preventing burnout?", 
    desc: "Use personalized assessments to identify your early warning signs and build a proactive maintenance plan." 
  },
  { 
    title: "Need manager support?", 
    desc: "Learn how to frame your needs to your manager, with templates and guidance provided by Cognito." 
  }
];

export default function ForEmployees() {
  return (
    <main className="cw-employees">
      <Navbar />
      
      <section className="cw-section" style={{ paddingTop: 'var(--space-xl)', textAlign: 'center' }}>
        <div className="cw-section__inner">
          <motion.div variants={staggerContainer(0.1)} initial="hidden" animate="visible">
            <motion.p className="cw-eyebrow" variants={fadeUp}>For You</motion.p>
            <motion.h1 variants={fadeUp} style={{ fontSize: '3rem', marginBottom: 'var(--space-md)' }}>
              Your wellbeing, on your terms.
            </motion.h1>
            <motion.p variants={fadeUp} style={{ fontSize: '1.2rem', maxWidth: '650px', margin: '0 auto var(--space-md)', opacity: 0.8 }}>
              Cognito is your private, confidential space to get support, build resilience, and navigate work challenges. No gatekeepers. No judgment.
            </motion.p>
            <div style={{ display: 'flex', justifyContent: 'center' }}><Waveform /></div>
            <motion.a href="#access" className="cw-btn cw-btn--primary" variants={fadeUp} {...btnMotion}>Access Cognito</motion.a>
          </motion.div>
        </div>
      </section>

      <section className="cw-section cw-section--alt">
        <div className="cw-section__inner">
          <motion.p className="cw-eyebrow" style={{ textAlign: 'center' }}>What you can do</motion.p>
          <motion.h2 className="cw-section__title" style={{ textAlign: 'center', fontSize: '2.2rem', marginBottom: 'var(--space-lg)' }}>
            Support tailored to your moment.
          </motion.h2>
          <motion.div variants={staggerContainer(0.1)} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--space-md)' }}>
            {USE_CASES.map((uc) => (
              <motion.div key={uc.title} className="cw-card" variants={fadeUp}>
                <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.3rem', marginBottom: 'var(--space-xs)' }}>{uc.title}</h3>
                <p style={{ opacity: 0.8, fontSize: '0.95rem' }}>{uc.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="cw-section">
        <div className="cw-section__inner" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-xl)', alignItems: 'center' }}>
          <motion.div variants={staggerContainer(0.1)} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.p className="cw-eyebrow" variants={fadeUp}>Your Privacy</motion.p>
            <motion.h2 variants={fadeUp} style={{ fontSize: '2.2rem', marginBottom: 'var(--space-md)' }}>
              What your employer can (and cannot) see.
            </motion.h2>
            <motion.ul variants={staggerContainer(0.08)} style={{ listStyle: 'none', display: 'grid', gap: 'var(--space-sm)' }}>
              {[
                "They CANNOT see who is using the platform.",
                "They CANNOT read your chat logs, assessment answers, or coaching notes.",
                "They CANNOT know if you are utilizing coaching or support resources.",
                "We DO NOT record audio or video sessions. Ever.",
                "They CAN see anonymous, company-wide trends (e.g., 'Engineering team stress levels are elevated') to improve workplace conditions."
              ].map((item, i) => (
                <motion.li key={i} className="cw-card" variants={fadeUp} style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'flex-start' }}>
                  <span style={{ color: i < 4 ? 'var(--cw-pink)' : 'var(--cw-teal)', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {i < 4 ? '✕' : '✓'}
                  </span>
                  <span style={{ fontSize: '0.95rem', lineHeight: '1.5' }}>{item}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
          
          <motion.div 
            className="cw-card" 
            variants={fadeUp} 
            initial="hidden" 
            whileInView="visible" 
            viewport={{ once: true }} 
            style={{ background: 'var(--cw-navy)', color: 'var(--cw-offwhite)', padding: 'var(--space-lg)', textAlign: 'center' }}
          >
            <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.5rem', marginBottom: 'var(--space-sm)' }}>
              Reassuring, non-clinical, and yours.
            </h3>
            <p style={{ opacity: 0.8, marginBottom: 'var(--space-md)', fontSize: '0.95rem' }}>
              Using Cognito is completely voluntary. It is never tied to your performance review, promotion eligibility, or employment status. Your growth is your own.
            </p>
            <a href="#access" className="cw-btn" style={{ background: 'var(--cw-offwhite)', color: 'var(--cw-navy)' }}>Get Started</a>
          </motion.div>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}